import React from 'react';
import { Joyride, Step, STATUS, EventData, TooltipRenderProps } from 'react-joyride';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const CustomTooltip: React.FC<TooltipRenderProps> = ({
  index,
  isLastStep,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
}) => {
  return (
    <div
      {...tooltipProps}
      className="bg-card text-card-foreground p-5 rounded-xl shadow-2xl border border-border max-w-sm space-y-3.5 select-none"
    >
      {/* Header: Title & Close Button */}
      <div className="flex items-start justify-between gap-3">
        {step.title && (
          <h4 className="font-bold text-sm tracking-tight text-foreground">
            {step.title}
          </h4>
        )}
        <button
          {...closeProps}
          type="button"
          className="text-muted-foreground hover:text-foreground p-1 rounded-md transition-colors -mr-1 -mt-1"
          title="Fechar Tour"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body: Content */}
      <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
        {step.content}
      </div>

      {/* Footer: Progress & Navigation Buttons */}
      <div className="flex items-center justify-between pt-2.5 border-t border-border/50 gap-2">
        {/* Left: Skip button (subtle text link on all steps) */}
        <button
          {...skipProps}
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors focus-visible:outline-none"
        >
          {isLastStep ? 'Ficar nas conversas' : 'Pular tour'}
        </button>

        {/* Center/Right Container */}
        <div className="flex items-center gap-3">
          {/* Step indicator */}
          <span className="text-[11px] font-medium text-muted-foreground/80">
            {index + 1} de {size}
          </span>

          <div className="flex items-center gap-1.5">
            {/* Back Button */}
            {index > 0 && (
              <button
                {...backProps}
                type="button"
                className="text-xs font-medium text-foreground hover:bg-muted px-2.5 py-1.5 rounded-md border border-border/80 transition-colors shadow-2xs"
              >
                Voltar
              </button>
            )}

            {/* Primary Action Button */}
            <button
              {...primaryProps}
              type="button"
              className="text-xs font-semibold text-primary-foreground bg-primary hover:bg-primary/90 px-3.5 py-1.5 rounded-md shadow-xs transition-colors"
            >
              {isLastStep ? 'Personalizar Meu Agente' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const OnboardingTour: React.FC = () => {
  const {
    isTourRunning,
    completeTour,
  } = useOnboardingStore();

  const navigate = useNavigate();

  const steps: Step[] = [
    {
      target: '#tour-conversation-list',
      title: 'Fila de Conversas & Status',
      content: 'Acompanhe o atendimento em tempo real. As conversas são categorizadas automaticamente entre "IA Atendendo", "Aguardando Humano" e "Fechada".',
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '#tour-conversation-card',
      title: 'Atendimento com Agente de IA 24/7',
      content: 'O Agente de IA consulta automaticamente seu catálogo e regras da loja para responder sobre prazos de entrega, frete e produtos.',
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '#tour-chat-header-actions',
      title: 'Atendimento Humano & Controle',
      content: 'Se o Agente de IA não souber responder ou o cliente pedir um atendente, o chat entra em "Aguardando Humano". Você pode assumir a conversa com 1 clique a qualquer momento.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '#tour-nav-customers',
      title: 'CRM de Clientes',
      content: 'Consulte o histórico completo, dados de contato e pedidos anteriores de quem já comprou ou falou com as suas lojas.',
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '#tour-nav-billing',
      title: 'Planos & Consumo',
      content: 'Acompanhe o consumo mensal de conversas da sua loja e ajuste o plano conforme suas vendas crescerem.',
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '#tour-nav-integrations',
      title: 'Configurações do Agente & Loja',
      content: 'Tudo pronto! Vamos personalizar o nome do seu agente, a saudação e as regras de atendimento da sua loja.',
      placement: 'right',
      skipBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data: EventData) => {
    const { status } = data;

    if (status === STATUS.FINISHED) {
      completeTour();
      navigate('/integrations?settings=true');
    } else if (status === STATUS.SKIPPED) {
      completeTour();
    }
  };

  if (!isTourRunning) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={isTourRunning}
      continuous
      onEvent={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      options={{
        primaryColor: 'hsl(var(--primary))',
        zIndex: 100000,
        arrowColor: 'hsl(var(--card))',
        backgroundColor: 'hsl(var(--card))',
        textColor: 'hsl(var(--card-foreground))',
        overlayColor: 'rgba(0, 0, 0, 0.65)',
      }}
    />
  );
};
